import { useEffect } from 'react';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  structuredData?: object | object[];
  breadcrumbs?: BreadcrumbItem[];
}

export const SOCIAL_IMAGE =
  'https://storage.readdy-site.link/project_files/c4084cf6-f9fc-491d-816c-98ce35896c67/ed00179a-e1fa-44ba-8d15-dd2eda444f6e_Screenshot-2026-04-09-at-10.10.07-PM.png';

const DEFAULT_OG_IMAGE = SOCIAL_IMAGE;

function setMeta(name: string, content: string, property = false) {
  const attr = property ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setStructuredData(id: string, data: object | object[]) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  structuredData,
  breadcrumbs,
}: SEOProps) {
  useEffect(() => {
    document.title = title;

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    // Open Graph
    setMeta('og:title', ogTitle ?? title, true);
    setMeta('og:description', ogDescription ?? description, true);
    setMeta('og:image', ogImage, true);
    setMeta('og:image:secure_url', ogImage, true);
    setMeta('og:type', ogType, true);
    if (canonical) {
      setMeta('og:url', canonical, true);
      setLink('canonical', canonical);
    }

    // Twitter
    setMeta('twitter:title', ogTitle ?? title);
    setMeta('twitter:description', ogDescription ?? description);
    setMeta('twitter:image', ogImage);
    setMeta('twitter:card', 'summary_large_image');

    // Structured data
    if (structuredData) {
      setStructuredData('page-structured-data', structuredData);
    }

    // Breadcrumbs
    if (breadcrumbs && breadcrumbs.length > 0) {
      setStructuredData('page-breadcrumb-data', buildBreadcrumbSchema(breadcrumbs));
    }

    return () => {
      document.title =
        'CSCF Support | Cardiospondylocarpofacial Syndrome Community & Resources';
      const sd = document.getElementById('page-structured-data');
      if (sd) sd.remove();
      const bc = document.getElementById('page-breadcrumb-data');
      if (bc) bc.remove();
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogType, structuredData, breadcrumbs]);
}
